import { makeStyles, alpha } from '@material-ui/core/styles'
import {
  CARD_BORDER,
  CARD_SHADOW,
  MUTED_TEXT,
  PAGE_BACKGROUND,
} from 'pages/things-to-do/events/register/registration-with-payment-intents/redesign/styles'

// The private lesson confirmation shares its tokens with the group event and
// Virtual Coaching confirmations (page grey, card border and shadow, muted
// text, the navy tint check circle, a serif navy title, a numbered steps list)
// and takes fonts, colors and buttons from the `offeringsRefresh` theme.
export { PAGE_BACKGROUND, MUTED_TEXT }

// Same narrow column as the Virtual Coaching confirmation.
export const COLUMN_WIDTH = 630

const useStyles = makeStyles(theme => ({
  page: {
    // The nested theme's body typography still resolves to the base theme's
    // Inter, so set the refresh font here and let plain text inherit it.
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
  },
  column: {
    boxSizing: 'border-box',
    maxWidth: COLUMN_WIDTH,
    margin: '0 auto',
    padding: theme.spacing(5, 2, 8),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 6),
    },
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: theme.spacing(0, 0, 3),
    textAlign: 'center',
  },
  iconCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    marginBottom: theme.spacing(1.5),
    borderRadius: '50%',
    // Same navy tint as the other success icons.
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    '& svg': { fontSize: 32 },
  },
  iconCircleError: {
    backgroundColor: '#FDE8E8',
    color: theme.palette.error.main,
  },
  title: {
    margin: 0,
    fontWeight: 700,
    fontSize: 32,
    lineHeight: 1.2,
    color: theme.palette.primary.main,
    [theme.breakpoints.down('xs')]: { fontSize: 26 },
  },
  subtitle: {
    margin: theme.spacing(1, 0, 0),
    color: MUTED_TEXT,
    fontSize: 16,
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  // Student row under the summary card: a bordered soft panel with a navy
  // tint avatar, so it reads as its own block on the grey page.
  whoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(2, 2.5),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
    // SOFT_PANEL_BACKGROUND is the page grey, so white (with the border and
    // shadow) is what lifts the row off the page.
    backgroundColor: theme.palette.common.white,
    boxShadow: CARD_SHADOW,
  },
  whoAvatar: {
    '&&': {
      flexShrink: 0,
      width: 44,
      height: 44,
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.main,
      fontSize: 16,
      fontWeight: 700,
    },
  },
  whoText: { flex: 1, minWidth: 0 },
  whoName: {
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    lineHeight: 1.3,
    color: theme.palette.primary.main,
    overflowWrap: 'anywhere',
  },
  whoSub: {
    margin: theme.spacing(0.25, 0, 0),
    color: MUTED_TEXT,
    fontSize: 13,
    lineHeight: 1.4,
  },
  card: {
    boxSizing: 'border-box',
    padding: theme.spacing(3),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 24,
    backgroundColor: theme.palette.common.white,
    boxShadow: CARD_SHADOW,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.5, 2),
      borderRadius: 20,
    },
  },
  cardTitle: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.25,
    color: theme.palette.primary.main,
  },
  steps: {
    margin: theme.spacing(2.5, 0, 0),
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.75),
  },
  stepNumber: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: PAGE_BACKGROUND,
    color: MUTED_TEXT,
    fontSize: 14,
    fontWeight: 700,
  },
  stepNumberActive: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  stepTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  stepCaption: {
    margin: theme.spacing(0.25, 0, 0),
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
  },
  callout: {
    marginTop: theme.spacing(2.5),
  },
  // "We applied one of your lessons" callout: navy tinted well with a border
  // and a leading icon tile, so it stands out from the surrounding cards.
  creditNote: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    borderRadius: 16,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    fontSize: 15,
    lineHeight: 1.5,
    '& p': { margin: 0 },
  },
  creditIcon: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
    '& svg': { fontSize: 22 },
  },
  creditCount: {
    display: 'block',
    marginTop: theme.spacing(0.25),
    fontSize: 17,
    fontWeight: 800,
    color: theme.palette.primary.main,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
    },
  },
  button: {
    textTransform: 'none',
    minWidth: 200,
  },
  download: {
    marginTop: theme.spacing(2),
  },
}))

export default useStyles
